import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { superadminApi, type MarinaRow } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { RefreshCw, ChevronRight } from 'lucide-react';

const TIER_BADGE: Record<string, string> = {
  free: 'bg-slate-100 text-slate-700',
  starter: 'bg-gray-100 text-gray-700',
  growth: 'bg-blue-100 text-blue-700',
  group: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700',
};

export function SuperadminMarinasPage() {
  const [marinas, setMarinas] = useState<MarinaRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const PAGE_SIZE = 50;

  const load = useCallback(() => {
    setLoading(true);
    superadminApi.getMarinas({ page, limit: PAGE_SIZE })
      .then((data) => {
        setMarinas(data.marinas);
        setTotal(data.total);
      })
      .catch(() => toast.error('Failed to load marinas'))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Marinas</h1>
          <p className="text-sm text-gray-500 mt-1">{total} total across all organizations</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Marina</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Organization</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Plan</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Stripe</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Currency</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-400">Loading…</td>
                </tr>
              ) : marinas.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-400">No marinas found</td>
                </tr>
              ) : marinas.map((m) => {
                const org = m.organizations;
                const orgTier = org?.subscription_tier ?? '';
                return (
                  <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{m.name}</div>
                      <div className="text-xs text-gray-400">/{m.slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      {org ? (
                        <Link
                          to={`/superadmin/orgs/${m.organization_id}`}
                          className="text-brand-600 hover:underline text-sm"
                        >
                          {org.name}
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {orgTier && (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${TIER_BADGE[orgTier] ?? 'bg-gray-100 text-gray-600'}`}>
                          {orgTier}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {m.stripe_onboarding_complete ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Connected</span>
                      ) : (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 uppercase text-xs">
                      {m.payment_currency ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${m.is_active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                        {m.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {new Date(m.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/superadmin/orgs/${m.organization_id}`}
                        className="text-brand-600 hover:text-brand-700 flex items-center gap-1 text-xs font-medium"
                      >
                        Org <ChevronRight className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Page {page + 1} of {totalPages} ({total} total)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="text-sm px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="text-sm px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
